import clsx from 'clsx'
import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useIntl} from 'react-intl'
import Tooltip from 'react-tooltip-lite'
import {PHOTO_ENHANCEMENT_SERVICE_ID} from '../../../shared/constants'
import {ExecutionMode, BASIC_SETTING_ID} from '../constants'
import {SettingReferenceModel} from '../../setting-reference/models/SettingReferenceModel'
import {SettingReferenceStatus} from '../../setting-reference/constants'
import {
  FeatureDataType,
  WATERMARK_FEATURE_NAME,
  WaterMarkOptions,
} from '../../../modules/services/constants'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {
  getAllActiveSettingReference,
  selectAllActiveSettingReference,
} from '../../../shared/redux/sharedRedux'
import {ServiceModel} from '../../../modules/services/models/ServiceModel'
import WaterMarkSettingModal from '../../setting-reference/components/modals/waterMarkSettingModal'
import {WatermarkSettingModel} from '../../setting-reference/models/WatermarkSettingModel'
import {selectUser} from '../../../modules/auth'
import {ReferenceFeatureModel} from '../../setting-reference/models/ReferenceFeatureModel'

type SettingSelectionSectionProps = {
  service: ServiceModel
  onSettingChanged?: (newSetting: SettingReferenceModel, customFeature?: any) => void
  onAiModelChanged?: (model: ExecutionMode) => void
}

type CustomSettingModel = {
  featureId: number
  value: any
}

const SettingSelectionSection: FC<SettingSelectionSectionProps> = ({
  service,
  onSettingChanged,
  onAiModelChanged,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const onSettingChangedRef = useRef(onSettingChanged)
  onSettingChangedRef.current = onSettingChanged
  const allSettings: SettingReferenceModel[] = useAppSelector(selectAllActiveSettingReference)
  const userInfo = useAppSelector(selectUser)
  const [selectedAiModel, setSelectedAiModel] = useState<ExecutionMode>(ExecutionMode.STANDARD)
  const [customFeatures, setCustomFeatures] = useState<CustomSettingModel[]>([]) //HOLD
  const [targetSetting, setTargetSetting] = useState<SettingReferenceModel | null>(null)
  const [watermarkModalState, setWatermarkModalState] = useState<{
    show: boolean
    featureId?: number
    setting?: WatermarkSettingModel
    baseImage?: string
    settingData?: any[]
  }>({show: false})
  const findServiceFeature = useCallback((featureId: number) =>
    service.features?.find((sf) => sf.id === featureId), [service.features])

  const settings: SettingReferenceModel[] = useMemo(() => {
    // only get active setting.
    let activeSettings = allSettings.filter(
      (setting) =>
        !setting.isDefault &&
        setting.status === SettingReferenceStatus.ACTIVE &&
        setting.serviceId === service.id
    )
    //get default setting first
    const defaultSettings = allSettings.filter(
      (setting) =>
        setting.isDefault && setting.serviceId === service.id &&   setting.status === SettingReferenceStatus.ACTIVE
    ).sort((a, b) => (b.userId ?? 0) - (a.userId ?? 0)) //system default first
    if (defaultSettings.length > 0) {
      activeSettings = [...defaultSettings, ...activeSettings]
    }
    return activeSettings
  }, [allSettings, service.id])

  useEffect(() => {
    if (!settings || settings.length === 0) {
      return
    }
    const selectedSetting = settings[0]
    const isPhotoEnhancement = service.id === PHOTO_ENHANCEMENT_SERVICE_ID
    let initialCustomFeatures: any[] = []
    if (!isPhotoEnhancement) {
      initialCustomFeatures =
        selectedSetting?.settingFeatures
          ?.map((feature) => {
            const serviceFeature = findServiceFeature(feature.featureId)
            return (
              serviceFeature?.isBasicSetting && {
                featureId: feature.featureId,
                value: (feature.value as string) || '',
              }
            )
          })
          .filter(Boolean) ?? []
    }
    setCustomFeatures(initialCustomFeatures)
    onSettingChangedRef.current?.(selectedSetting, initialCustomFeatures)
    setTargetSetting(selectedSetting)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, service, findServiceFeature])
  const isPhotoEnhancement = service.id === PHOTO_ENHANCEMENT_SERVICE_ID
  useEffect(() => {
    if (service) {
      dispatch(getAllActiveSettingReference(service.id))
    }
  }, [dispatch, service])

  const getSettingDisplayName = (setting: SettingReferenceModel) => {
    return setting.userId ? setting.name : intl.formatMessage({id: setting.name})
  }

  const closeWatermarkModal = (isCancel: boolean = false) => {
    setWatermarkModalState({show: false})
    if (isCancel) {
      //remove the watermark custom feature if exist
      const nextCustomFeatures = [...customFeatures]
      const targetIndex = nextCustomFeatures.findIndex(
        (item) => item.featureId === watermarkModalState.featureId
      )
      if (targetIndex >= 0) {
        nextCustomFeatures.splice(targetIndex, 1)
        setCustomFeatures(nextCustomFeatures)
        const currentSetting = targetSetting ?? settings[0]
        if (currentSetting) {
          onSettingChanged?.(currentSetting, nextCustomFeatures)
        }
      }
    }
  }

  const handleWatermarkSave = (setting: WatermarkSettingModel) => {
    if (!watermarkModalState.featureId) {
      closeWatermarkModal()
      return
    }

    const nextCustomFeatures = [...customFeatures]
    const targetIndex = nextCustomFeatures.findIndex(
      (item) => item.featureId === watermarkModalState.featureId
    )
    const value = JSON.stringify(setting)
    if (targetIndex >= 0) {
      nextCustomFeatures[targetIndex].value = value
    } else {
      nextCustomFeatures.push({featureId: watermarkModalState.featureId, value})
    }

    setCustomFeatures(nextCustomFeatures)
    const currentSetting = targetSetting ?? settings[0]
    if (currentSetting) {
      onSettingChanged?.(currentSetting, nextCustomFeatures)
    }
    closeWatermarkModal()
  }

  const handleManualSettingChange = (settingId: number) => {
    const target = settings.find((setting) => setting.id === settingId)
    if (!target) {
      return
    } else {
      onSettingChanged?.(target)
      setTargetSetting(target)
    }
  }
  const handleCustomFeatureCheck = (
    feature: ReferenceFeatureModel,
    isChecked: any,
    value: any,
    setting: SettingReferenceModel
  ) => {
    let cloneNewCustomFeatures = [...customFeatures]
    const targetIndex = cloneNewCustomFeatures.findIndex(
      (item) => item.featureId === feature.featureId
    )
    if (isChecked) {
      let defaultValue: any = value
      if (WATERMARK_FEATURE_NAME === feature.featureName) {
        if (defaultValue === WaterMarkOptions.DEFAULT) {
          defaultValue = JSON.stringify(userInfo?.watermarkInfo)
        }
        if (defaultValue === WaterMarkOptions.CUSTOM) {
          try {
            defaultValue = JSON.parse(feature.value as string)
          } catch (err) {
            //ignore
          }
          setWatermarkModalState({
            show: true,
            featureId: feature.featureId,
            setting: defaultValue,
            baseImage: service.imgBefore,
            settingData: findServiceFeature(feature.featureId)?.data,
          })
        }
      }
      if (targetIndex >= 0) {
        cloneNewCustomFeatures[targetIndex].value = defaultValue
      } else {
        cloneNewCustomFeatures.push({
          featureId: feature.featureId,
          value: defaultValue,
        })
      }
    } else if (targetIndex >= 0) {
      cloneNewCustomFeatures.splice(targetIndex, 1)
    }
    setCustomFeatures(cloneNewCustomFeatures)
    onSettingChanged?.(setting, cloneNewCustomFeatures)
  }
  const handleCustomFeatureOptionChange = (
    feature: ReferenceFeatureModel,
    value: any,
    setting: SettingReferenceModel
  ) => {
    const serviceFeature = findServiceFeature(feature.featureId)
    if (serviceFeature?.dataType === FeatureDataType.WATERMARK) {
      if (value === WaterMarkOptions.CUSTOM) {
        let watermarkSettingDefault = undefined
        try {
          watermarkSettingDefault = JSON.parse(feature.value as string)
        } catch (err) {
          //ignore
        }
        setWatermarkModalState({
          show: true,
          featureId: feature.featureId,
          setting: watermarkSettingDefault,
          baseImage: serviceFeature.imgBefore ?? service.imgBefore,
          settingData: serviceFeature.data,
        })
        return
      }
      if (value === WaterMarkOptions.DEFAULT) {
        value = userInfo?.watermarkInfo
      }
    }
    let cloneNewCustomFeatures = [...customFeatures]
    const targetIndex = cloneNewCustomFeatures.findIndex(
      (item) => item.featureId === feature.featureId
    )
    if (targetIndex >= 0) {
      cloneNewCustomFeatures[targetIndex].value = value
    } else {
      cloneNewCustomFeatures.push({
        featureId: feature.featureId,
        value: value,
      })
    }
    setCustomFeatures(cloneNewCustomFeatures)
    onSettingChanged?.(setting, cloneNewCustomFeatures)
  }
  // Render functions
  const handleAiModelChange = (model: ExecutionMode) => {
    setSelectedAiModel(model)
    onAiModelChanged?.(model)
    if (model === ExecutionMode.STANDARD && settings.length > 0) {
      const defaultSetting = settings[0]
      setTargetSetting(defaultSetting)
      setCustomFeatures([])
      onSettingChanged?.(defaultSetting, [])
    }
    if (model === ExecutionMode.LITE && settings.length > 0) {
      const basicSetting = settings.find((s) => s.id === BASIC_SETTING_ID) ?? settings[0]
      setTargetSetting(basicSetting)
      setCustomFeatures([])
      onSettingChanged?.(basicSetting, [])
    }
  }

  const renderClassicLayout = () => (
    <div className='d-flex flex-column gap-3'>
      {/* AI Model Selection */}
      {/* <div className='d-flex align-items-center gap-2 text-gray-700 fw-semibold'>
        <span className='bullet me-1'></span>
        <span className='fs-7'>
          {intl.formatMessage({id: 'Order.executionMode.TITLE'})}
        </span>
      </div> */}
      {/* <div className='row g-3'>
        <div className='col-6'>
          <label
            className={clsx(
              'border rounded-3 p-4 cursor-pointer w-100 bg-white shadow-sm d-flex flex-column',
              selectedAiModel === ExecutionMode.STANDARD && 'border-primary border-2'
            )}
          >
            <div className='form-check form-check-custom form-check-solid'>
              <input
                type='radio'
                className='form-check-input me-3'
                name='ai-model-selection'
                checked={selectedAiModel === ExecutionMode.STANDARD}
                onChange={() => handleAiModelChange(ExecutionMode.STANDARD)}
              />
              <span className='form-check-label fw-bold text-gray-900 d-flex align-items-center gap-2'>
                <i className='bi bi-stars text-primary'></i>
                {intl.formatMessage({id: 'Order.executionMode.STANDARD'})}
              </span>
            </div>
            <span className='text-muted fs-8 mt-2 ps-8'>
              {intl.formatMessage({id: 'Order.executionMode.STANDARD.DESC'})}
            </span>
          </label>
        </div>
        <div className='col-6'>
          <label
            className={clsx(
              'border rounded-3 p-4 cursor-pointer w-100 bg-white shadow-sm d-flex flex-column',
              selectedAiModel === ExecutionMode.LITE && 'border-primary border-2'
            )}
          >
            <div className='form-check form-check-custom form-check-solid'>
              <input
                type='radio'
                className='form-check-input me-3'
                name='ai-model-selection'
                checked={selectedAiModel === ExecutionMode.LITE}
                onChange={() => handleAiModelChange(ExecutionMode.LITE)}
              />
              <span className='form-check-label fw-bold text-gray-900 d-flex align-items-center gap-2'>
                <i className='bi bi-lightning-charge-fill text-warning'></i>
                {intl.formatMessage({id: 'Order.executionMode.LITE'})}
              </span>
            </div>
            <span className='text-muted fs-8 mt-2 ps-8'>
              {intl.formatMessage({id: 'Order.executionMode.LITE.DESC'})}
            </span>
          </label>
        </div>
      </div> */}

      {/* Setting Selection */}
      {selectedAiModel !== ExecutionMode.LITE && (
        <>
          <div className='d-flex align-items-center gap-2 text-gray-700 fw-semibold'>
            <span className='bullet me-1'></span>
            <span className='fs-7'>
              {intl.formatMessage({id: 'Order.setting.PREFIX_SETTING.DESC'})}
            </span>
          </div>
          <select
            data-placeholder={intl.formatMessage({
              id: 'Order.setting.PREFIX_SETTING.Placeholder',
            })}
            className='form-select form-select-sm form-select-solid'
            value={targetSetting?.id ?? ''}
            onChange={(e: any) => handleManualSettingChange(Number(e.target.value))}
          >
            {settings.map((setting) => {
              return (
                <option key={setting.id} value={setting.id}>
                  {getSettingDisplayName(setting)}
                </option>
              )
            })}
          </select>
          <div>
            <a href='/setting-references' className='link-primary fs-7 fw-semibold'>
              {intl.formatMessage({id: 'Order.setting.PREFIX_SETTING.CUSTOMS'})}
            </a>
          </div>
        </>
      )}
    </div>
  )

  const renderRadioLayout = () => (
    <div className='row g-4 align-items-stretch'>
      <div className='col-12'>
        <div className='d-flex align-items-center gap-2 text-gray-700 fw-semibold'>
          <span className='bullet me-1'></span>
          <span className='fs-7'>
            {intl.formatMessage({id: 'Order.setting.PREFIX_SETTING.DESC'})}
          </span>
        </div>
        <div className='mt-4'>
          {settings.length > 0 ? (
            <div className='d-flex flex-column'>
              {settings.map((setting) => {
                const isSelected = targetSetting?.id === setting.id
                return (
                  <label
                    key={setting.id}
                    className={clsx(
                      'border rounded-3 p-4 mb-3 cursor-pointer w-100 bg-white shadow-sm',
                      isSelected && 'border-primary border-2'
                    )}
                  >
                    <div className='form-check form-check-custom form-check-solid'>
                      <input
                        type='radio'
                        className='form-check-input me-3'
                        name='order-setting-options'
                        value={setting.id}
                        checked={isSelected}
                        onChange={() => handleManualSettingChange(setting.id)}
                      />
                      <span className='form-check-label fw-bold text-gray-900'>
                        {getSettingDisplayName(setting)}
                      </span>
                    </div>
                    {renderSettingFeatureControls(setting)}
                  </label>
                )
              })}
            </div>
          ) : (
            <div className='alert alert-secondary mb-0'>
              {intl.formatMessage({id: 'SYSTEM.NO_DATA'})}
            </div>
          )}
        </div>
      </div>
    </div>
  )
  const renderSettingFeatureControls = (setting: SettingReferenceModel) => {
    if (!setting.settingFeatures || setting.settingFeatures.length === 0) {
      return null
    }
    const renderedControls = setting.settingFeatures
      .map((feature) => {
        const serviceFeature = findServiceFeature(feature.featureId)
        let options: {key: string; value: string}[] = serviceFeature ? serviceFeature.data : []
        if (serviceFeature?.dataType === FeatureDataType.WATERMARK) {
          options = []
          if (userInfo?.watermarkInfo?.watermark) {
            options.push({
              key: WaterMarkOptions.DEFAULT,
              value: 'Order.setting.DEFAULT',
            })
          }
          options.push({
            key: WaterMarkOptions.CUSTOM,
            value: 'Order.setting.CUSTOM',
          })
        }
        const isDefaultSetting = serviceFeature?.isBasicSetting
        if (!options || options.length === 0) {
          return null
        }
        const selectedItem = customFeatures?.find((item) => item.featureId === feature.featureId)
        const isActive = selectedItem !== undefined

        return (
          <div className='col-12 col-md-12' key={`${setting.id}-${feature.featureId}`}>
            <div className='mt-4'>
              <div className='d-flex align-items-center mb-2 gap-3'>
                {!isDefaultSetting && (
                  <div className='form-check form-check-solid form-switch mb-0'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      role='switch'
                      checked={isActive}
                      disabled={isDefaultSetting}
                      onChange={(e) => {
                        const isChecked = e.target.checked
                        handleCustomFeatureCheck(
                          feature,
                          isChecked,
                          options[0].key.toString(),
                          setting
                        )
                      }}
                    />
                  </div>
                )}
                <span className='fw-semibold text-gray-700'>
                  {intl.formatMessage({id: 'SERVICE.FEATURE.' + feature.featureName})}
                </span>
              </div>
              {/* setting.dataType === FeatureDataType.WATERMARK */}
              {/* if  setting is watermark  render special control: it a select with only 2 option : Default and Custom  */}
              {/* when click custom open WaterMarkSettingModal */}
              <select
                disabled={!selectedItem}
                className='form-select form-select-sm form-select-solid'
                value={selectedItem?.value || ''}
                onChange={(e) => handleCustomFeatureOptionChange(feature, e.target.value, setting)}
              >
                {options.map((option) => {
                  const optionValue = option.key?.toString() ?? ''
                  return (
                    <option value={optionValue} key={optionValue}>
                      {intl.formatMessage({id: option.value})}
                    </option>
                  )
                })}
              </select>
            </div>
          </div>
        )
      })
      .filter((control): control is JSX.Element => control !== null)

    if (renderedControls.length === 0) {
      return null
    }

    return (
      <div className='mt-3 ps-2 ps-lg-7'>
        <div className='row g-4'>{renderedControls}</div>
      </div>
    )
  }
  return (
    <>
      <div className='mb-5 fv-row'>
        <div className='d-flex align-items-center mb-1 fw-bolder'>
          <label className='form-label mb-0 fw-bolder'>
            {intl.formatMessage({id: 'Order.setting.TITLE'})}
          </label>
          <Tooltip content={intl.formatMessage({id: 'Order.setting.DESC'})} className='tooltips'>
            <i className='text-gray-400 fas fa-exclamation-circle ms-1 mb-3 fs-7'></i>
          </Tooltip>
        </div>
        {isPhotoEnhancement ? renderClassicLayout() : renderRadioLayout()}
      </div>

      {watermarkModalState.show && watermarkModalState.setting && (
        <WaterMarkSettingModal
          isShow={watermarkModalState.show}
          onClose={() => closeWatermarkModal(true)}
          onChanged={handleWatermarkSave}
          setting={watermarkModalState.setting}
          baseImage={watermarkModalState.baseImage ?? service.imgBefore}
          settingData={watermarkModalState.settingData}
          className=''
        />
      )}
    </>
  )
}

export {SettingSelectionSection}
